import type { UseQueryResult, DefaultError } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import { getSubjectAreasBasePath, getSubjectAreasPath } from '../utils/api-paths';
import type {
  SubjectArea,
  SubjectAreasResponse,
  SubjectAreaFilters,
  CreateSubjectAreaPayload,
  DeleteSubjectAreaPayload,
  UpdateSubjectAreaPayload,
} from '../types';

/**
 * Fetch all subject areas with optional filters
 * @param filters - Query filters (e.g., getLockedStatus)
 */
export function useGetSubjectAreas(
  filters?: SubjectAreaFilters
): UseQueryResult<SubjectAreasResponse, DefaultError> {
  const apiClient = useApiClient();
  const basePath = getSubjectAreasBasePath();

  return useQuery({
    queryKey: queryKeys.subjectAreas.list(filters),
    queryFn: async () => {
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
        const mockData = await import('../mock-data/subject-areas.json');
        const data = mockData.default as SubjectArea[];
        return { data, total: data.length };
      }

      const response = await apiClient.get<SubjectArea[]>(basePath, { params: filters });
      return {
        data: response.data,
        total: response.data.length,
      };
    },
    retry: false,
    throwOnError: false,
  });
}

export function useCreateSubjectArea() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const basePath = getSubjectAreasBasePath();

  return useMutation({
    mutationFn: async (payload: CreateSubjectAreaPayload) => {
      const response = await apiClient.post<SubjectArea>(basePath, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjectAreas.lists() });
    },
  });
}

export function useDeleteSubjectArea() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const deletePath = getSubjectAreasPath('delete');

  return useMutation({
    mutationFn: async (payload: DeleteSubjectAreaPayload) => {
      const response = await apiClient.post<SubjectArea>(deletePath, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjectAreas.lists() });
    },
  });
}

export function useUpdateSubjectArea() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const updatePath = getSubjectAreasPath('update');

  return useMutation({
    mutationFn: async (payload: UpdateSubjectAreaPayload) => {
      const response = await apiClient.post<SubjectArea>(updatePath, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjectAreas.lists() });
    },
  });
}
